import { InlineAlert } from '../../components/BaseComponents'
import { useRef, useState, useEffect } from "react";
import { Button, LoadingSpinner } from "../../components/BaseComponents";
import CanonicalModal from "../../components/BaseComponents/CanonicalModal/CanonicalModal";
import useApi from "../../hooks/useApi";
import { getCSRFToken } from "../../lib/utils";
import { FOCUS_VISIBLE_RING_INSET } from "../../lib/a11yFocus";
import { getTrainingUsersTemplateMetadata, CsvTemplateMetadata } from "../../lib/api/csvTemplates";
import { CsvColumn } from "../../types/CsvTemplate";

type CsvRowError = {
    reference: string,
    text: string,
};

type UploadTrainingUsersModalProps = {
    trainingSessionId: string,
    onClose: () => void,
    onUploaded: () => void,
    isOpen?: boolean,
};

// Reuses AddPurchasesUpload.tsx's preview/per-row-error UX and the CSV template
// download + guidance accordion pattern from CompanyPurchases.tsx.
function UploadTrainingUsersModal({ trainingSessionId, onClose, onUploaded, isOpen }: UploadTrainingUsersModalProps) {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<CsvRowError[]>([]);
    const [showGuidance, setShowGuidance] = useState(false);
    const [templateMetadata, setTemplateMetadata] = useState<CsvTemplateMetadata | null>(null);
    const [templateLoading, setTemplateLoading] = useState(true);
    const [isDragActive, setIsDragActive] = useState(false);
    const [invalidFileType, setInvalidFileType] = useState(false);
    const inputFile = useRef<HTMLInputElement>(null);
    const api = useApi();

    // Fetch CSV template metadata from backend on component mount
    useEffect(() => {
        const fetchTemplate = async () => {
            try {
                const metadata = await getTrainingUsersTemplateMetadata();
                setTemplateMetadata(metadata);
            } catch (error) {
                console.warn('Failed to fetch CSV template metadata:', error);
                // Graceful degradation: if API fails, continue with null (fallback UI will render)
                setTemplateMetadata(null);
            } finally {
                setTemplateLoading(false);
            }
        };

        fetchTemplate();
    }, []);

    const reset = () => {
        setFile(null);
        setErrors([]);
        setInvalidFileType(false);
        if (inputFile.current) {
            inputFile.current.value = '';
        }
    }

    const handleFileSelection = (selectedFile: File | null) => {
        setErrors([]);
        if (selectedFile) {
            if (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv')) {
                setFile(selectedFile);
                setInvalidFileType(false);
            } else {
                setInvalidFileType(true);
                setFile(null);
            }
        } else {
            setFile(null);
            setInvalidFileType(false);
        }
    }

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    }

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles.length > 0) {
            handleFileSelection(droppedFiles[0]);
        }
    }

    const upload = async () => {
        if (!file) {
            return;
        }
        setLoading(true);
        setErrors([]);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('_csrf', await getCSRFToken());

            const body = await api.form(
                `admin/training-sessions/${trainingSessionId}/users/bulk-upload`,
                formData,
            );

            if (body.success === true) {
                reset();
                onUploaded();
            } else {
                setErrors(body.errors || []);
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <CanonicalModal
            open={!!isOpen}
            onClose={() => { reset(); onClose(); }}
            size="small"
            title="Import users (CSV)"
            isDragActive={isDragActive}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            actions={
                <>
                    <Button
                        onClick={upload}
                        additionalClass="mr-5"
                        disabled={!file || loading}
                        type="Primary"
                        text="Upload"
                    />
                    <Button onClick={() => { reset(); onClose(); }} type="Outlined" text="Cancel" />
                </>
            }
        >
            <div className="flex flex-wrap gap-4 mt-4">
                <Button
                    onClick={() => {
                        window.location.href = `${process.env.REACT_APP_BASE_API}/admin/training-sessions/users-csv-template`
                    }}
                    type="Primary"
                    hasLeadingIcon
                    icon="fa-solid fa-download"
                    text="Download CSV template"
                />
                <input
                    type="file"
                    id="trainingUsersCsvFile"
                    aria-label="Select CSV file to upload"
                    ref={inputFile}
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => {
                        handleFileSelection(e.target.files && e.target.files.length > 0 ? e.target.files[0] : null);
                    }}
                />
                <Button
                    onClick={() => inputFile.current?.click()}
                    type="Outlined"
                    text="Select CSV file..."
                    hasLeadingIcon
                    icon="fa-solid fa-upload"
                />
            </div>

            <p className="text-charcoal-100 text-sm mt-4">(You can drag & drop a CSV file onto this area)</p>

            <div className="mt-6 rounded-lg bg-charcoal-700 p-4 text-white">
                <button
                    type="button"
                    aria-controls="trainingUsersCsvGuidance"
                    aria-expanded={showGuidance}
                    className={`flex items-center justify-between w-full text-left cursor-pointer px-1.5 ${FOCUS_VISIBLE_RING_INSET}`}
                    onClick={() => setShowGuidance(!showGuidance)}
                >
                    <h2 className="text-sm">Data collection and CSV format</h2>
                    <i className={`fa-solid ${showGuidance ? 'fa-minus' : 'fa-plus'} text-algae-300`} />
                </button>
                <div
                    id="trainingUsersCsvGuidance"
                    className={`${!showGuidance && 'hidden'} border-t border-charcoal-400 mt-4 pt-2 text-sm`}
                >
                    {templateLoading ? (
                        <p className="text-charcoal-100 py-2">Loading field information...</p>
                    ) : templateMetadata?.template && templateMetadata.template.length > 0 ? (
                        <>
                            <ul className="pl-4 list-disc">
                                {templateMetadata.template.map((column: CsvColumn) => (
                                    <li key={column.name} className="mb-2 text-charcoal-100">
                                        <span className="bg-charcoal-400 rounded px-1.5 py-0.5 text-white text-sm font-mono">{column.name}</span> {column.description || column.name}
                                        {column.isRequired ? (
                                            <span className="text-algae-600"> (MANDATORY)</span>
                                        ) : (
                                            <span className="text-algae-600"> (optional)</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            <p className="text-charcoal-100 mt-2">
                                Max {templateMetadata.maxRowsPerUpload} rows per upload. Any row error rejects the whole file – fix and re-upload.
                            </p>
                        </>
                    ) : (
                        <>
                            <ul className="pl-4 list-disc">
                                <li className="mb-2 text-charcoal-100">
                                    <span className="bg-charcoal-400 rounded px-1.5 py-0.5 text-white text-sm font-mono">EMAIL</span> user email address
                                    <span className="text-algae-600"> (MANDATORY)</span>
                                </li>
                                <li className="mb-2 text-charcoal-100">
                                    <span className="bg-charcoal-400 rounded px-1.5 py-0.5 text-white text-sm font-mono">organization_name</span> custom organisation name
                                    <span className="text-algae-600"> (optional)</span>
                                </li>
                            </ul>
                            <p className="text-charcoal-100 mt-2">
                                Max 100 rows per upload. Any row error rejects the whole file – fix and re-upload.
                            </p>
                        </>
                    )}
                </div>
            </div>

            { loading && <LoadingSpinner /> }
            { invalidFileType && (
                <div className="bg-warning-050 text-warning-800 rounded p-4 mt-4 text-sm">
                    <p>Only CSV files are supported.</p>
                </div>
            )}
            { file && !errors.length && (
                <div className="bg-charcoal-700 text-charcoal-100 rounded p-4 mt-4 text-sm">
                    <p>File selected: <span className="bg-charcoal-400 rounded px-2 py-1 text-white font-mono">{file.name}</span></p>
                </div>
            )}
            { errors.length > 0 && (
                <InlineAlert type='error' className='mt-4'>                    <p className="mb-2">This file was rejected - no users were created:</p>
                    <ul className="list-disc pl-4">
                        { errors.map((error, index) => (
                            <li key={`${error.reference}-${index}`}>
                                <strong>{ error.reference }</strong>: { error.text }
                            </li>
                        ))}
                    </ul>
                </InlineAlert>
            )}
        </CanonicalModal>
    )
}

export default UploadTrainingUsersModal;

